import { CloseIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Grid, Link, Text } from "@chakra-ui/react";
import apiDigitacao from "api/api-digitacao";
import { Attendance } from "components/atendimentos-components/atendimento-form/types";
import { CustomModal } from "components/custom-modal";
import DynamicTable from "components/dynamic-table";
import { Column } from "components/dynamic-table/types";
import { useState } from "react";
import { makeEvent, useEventListener } from "services/events";
import { DigitadoRow } from "..";
import { Toast } from "components/toast";
import {
  bgError,
  bgSucess,
  bgWarning,
  StatusFormat
} from "components/dynamic-table/row-status";
import { bancosConsigCLT } from "../../../margens/emprestimo-consignado-clt";

interface StatusDigitacaoRow {
  datetime: string;
  status: string;
  description?: string;
}

interface ConsultaPropostaData {
  banco?: string | null;
  status?: null | "PENDENT" | "FINISHED";
  codeRequestInss?: string | null;
  codeRequestDigitacao?: string | null;
  numeroProposta?: string | null;
  urlFormalizacao?: string | null;
  linkFormalizacao?: string | null;
  message?: string | null;
  messageDigitacao?: string | null;
  valorLiberadoCliente?: number;
  ade?: string | null;
}

interface ModalInitialProps {
  row: DigitadoRow;
  onChangeRow: (k: keyof DigitadoRow | null, v: any) => void;
  statusList?: StatusDigitacaoRow[];
}

export function ModalConsultarProposta({
  attendance
}: {
  attendance: Attendance;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  let [modalData, setModalData] = useState<Partial<ModalInitialProps>>({});
  const [consultaProposta, setConsultaProposta] =
    useState<ConsultaPropostaData>({});

  const onOpen = async (props: ModalInitialProps) => {
    setIsOpen(true);
    getStatusList(props.row);
    onChange(null, props);
  };

  const onClose = () => {
    setIsOpen(false);
    setModalData({});
  };

  const onChange = (k: keyof ModalInitialProps | null, value: any) => {
    if (k == null) {
      modalData = value;
      setModalData(modalData);
    } else {
      modalData = { ...modalData, [k]: value };
      setModalData(modalData);
    }
  };

  const getStatusList = async (row: DigitadoRow) => {
    let keys: {
      [k: string]: keyof DigitadoRow;
    } = {
      urlFormalizacao: "linkFormalizacao",
      linkFormalizacao: "linkFormalizacao",
      numeroProposta: "ade",
      ade: "ade",
      codeRequestInss: "codeRequestDigitacao",
      codeRequestDigitacao: "codeRequestDigitacao",
      message: "messageDigitacao",
      messageDigitacao: "messageDigitacao",
      status: "status",
      banco: "banco",
      valorLiberadoCliente: "valorLiquidoCliente",
      valorLiquidoCliente: "valorLiquidoCliente"
    };

    const url =
      row.convenio === "CONSIGNADO_TRABALHADOR"
        ? `/api/digitacoes/clt/atendimento/${attendance.id}/novo/consultar/${row.id}`
        : `/api/digitacoes/inss/atendimento/${attendance.id}/consultar/${row.id}`;

    setIsLoading(true);
    try {
      const { data } = await apiDigitacao.get<ConsultaPropostaData>(url);
      setConsultaProposta(data);

      Object.keys(data).forEach((k) => {
        let value = data[k as keyof ConsultaPropostaData];
        if (keys[k] && value != null) {
          if (k === "status") {
            if (value === "PENDENT") value = "PENDING";
            modalData.onChangeRow?.(keys[k], value);
          } else modalData.onChangeRow?.(keys[k], value);
        }
      });
    } catch (e) {
      Toast({
        title: "Erro ao consultar o acompanhamento da proposta",
        description: "Tente novamente",
        status: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEventListener("openModalConsultarProposta", onOpen);

  const modalFooter = (
    <>
      <Button
        variant="outline"
        leftIcon={<CloseIcon w="12px" h="12px" />}
        onClick={onClose}
      >
        Fechar
      </Button>
    </>
  );

  return (
    <CustomModal
      modalTitle="Consultar"
      isOpen={isOpen}
      onClose={onClose}
      modalFooter={modalFooter}
      size={"3xl"}
      isLoading={isLoading}
    >
      <Flex
        flexDir="column"
        border="1px solid #E8EAED"
        bg="#F5F7F9"
        p="10px 15px"
        borderRadius="10px"
        _hover={{ bg: "gray.100" }}
        transition="0.2s"
      >
        <Grid templateColumns="1fr 1fr 1fr 1fr" gap="10px">
          <Box>
            <Text fontWeight="bold" mb="8px">
              Banco:
            </Text>{" "}
            <Text whiteSpace="nowrap">
              {
                bancosConsigCLT.find(
                  ({ value }) => value === consultaProposta.banco
                )?.name
              }
            </Text>
          </Box>
          <Box>
            <Text fontWeight="bold" mb="8px">
              Status Fila:
            </Text>{" "}
            <StatusFormat
              bg={parseStatus.get(consultaProposta.status || "")?.color}
              whiteSpace="nowrap"
            >
              {parseStatus.get(consultaProposta.status || "")?.desc || ""}
            </StatusFormat>
          </Box>
          <Box gridArea="auto / span 2">
            <Text fontWeight="bold" mb="8px">
              Observação:
            </Text>{" "}
            <Text>
              {consultaProposta.message || consultaProposta.messageDigitacao}
            </Text>
          </Box>
          <Box>
            <Text fontWeight="bold" mb="8px">
              Status Digitação:
            </Text>{" "}
            <StatusFormat
              bg={
                parseCodigo.get(
                  consultaProposta.codeRequestInss ||
                    consultaProposta.codeRequestDigitacao ||
                    ""
                )?.color
              }
              whiteSpace="nowrap"
            >
              {parseCodigo.get(
                consultaProposta.codeRequestInss ||
                  consultaProposta.codeRequestDigitacao ||
                  ""
              )?.desc || ""}
            </StatusFormat>
          </Box>

          <Box>
            <Text fontWeight="bold" mb="8px">
              ADE:
            </Text>{" "}
            <Text whiteSpace="nowrap">
              {consultaProposta.numeroProposta || consultaProposta.ade}
            </Text>
          </Box>
          <Box gridArea="auto / span 2">
            <Text fontWeight="bold" mb="8px">
              URL Formalização:
            </Text>{" "}
            <Link
              target="_blank"
              href={
                consultaProposta.urlFormalizacao ||
                consultaProposta.linkFormalizacao ||
                ""
              }
            >
              {consultaProposta.urlFormalizacao ||
                consultaProposta.linkFormalizacao}
            </Link>
          </Box>
        </Grid>
      </Flex>
    </CustomModal>
  );
}

export const openModalConsultarProposta = (props: ModalInitialProps) =>
  makeEvent("openModalConsultarProposta", props);

const parseCodigo = new Map([
  ["SUCCESS", { desc: "Sucesso", color: bgSucess }],
  ["SYSTEM_ERROR", { desc: "Erro", color: bgError }]
]);
const parseStatus = new Map([
  ["PENDING", { desc: "Pendente", color: bgWarning }],
  ["PENDENT", { desc: "Pendente", color: bgWarning }],
  ["FINISHED", { desc: "Finalizado", color: bgSucess }],
  ["SYSTEM_ERROR", { desc: "Erro", color: bgError }]
]);
